import React from 'react'
import { Button, Divider, Popconfirm, Typography } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import { useBillingSelector } from 'store/billingSlice/billingReducer'
import { formatAmount } from 'utils'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import {
  cancelBillingSubscription,
  renewBillingSubscription,
} from 'store/billingSlice/billingActions'
import { useAuthSelector } from 'store/authSlice/authReducer'
import { Can } from 'permissions'
import { config } from 'configs'

const InfoCard = ({ setShowChangeTierModal, setShowPaymentMethodModal }) => {
  const dispatch = useDispatch()
  const { organization } = useAuthSelector()
  const {
    loading: { cancellingSubscription, renewingSubscription },
    subscription,
  } = useBillingSelector()

  const handleCancelSubscription = () => {
    const organizationId = organization?.id
    dispatch(cancelBillingSubscription(organizationId))
  }

  const handleRenewSubscription = () => {
    const organizationId = organization?.id
    dispatch(renewBillingSubscription(organizationId))
  }

  return (
    <>
      <div className="t-px-[30px] t-p-2">
        <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-bold t-text-[rgba(0,0,0,0.7)]">
          YOUR PLAN
        </Typography>
        <div className="t-flex t-justify-between t-mt-2">
          <div>
            <Typography className="t-text-roboto t-text-[28px] t-leading-[32.81px] t-font-extrabold">
              {subscription?.Tier?.TierName}
            </Typography>
            <Can
              I={config.actions.edit}
              a={config.components.organizationSubscription}
            >
              <div
                className="t-flex t-gap-1 t-items-center t-mt-1 t-cursor-pointer"
                onClick={() => setShowChangeTierModal(true)}
              >
                <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[#1BA1E2]">
                  Change plan
                </Typography>
                <RightOutlined
                  style={{ fontSize: '0.8rem' }}
                  className="t-text-[#1BA1E2]"
                />
              </div>
            </Can>
          </div>
          <div>
            <Typography className="t-text-roboto t-text-[22px] t-leading-[25.78px] t-font-bold">
              {formatAmount(subscription?.Tier?.BaseFee)}
            </Typography>
          </div>
        </div>
      </div>
      <Divider />
      <div className="t-px-[30px] t-p-2">
        <Typography className="t-text-roboto t-text-[18px] t-leading-[21.09px] t-font-bold t-text-[#000000]">
          Summary
        </Typography>
        {subscription?.SubscriptionStatus === 'Active' && (
          <div className="t-flex t-justify-between t-mt-5">
            <div>
              <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-medium t-text-[#000000]">
                Next Payment
              </Typography>
              <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0, 0, 0, 0.6)] t-mt-1">
                {moment(subscription?.NextPaymentDate).format('DD/MM/YYYY')}
              </Typography>
            </div>
            <div>
              <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-medium t-text-[#000000]">
                {formatAmount(subscription?.Tier?.BaseFee)}
              </Typography>
            </div>
          </div>
        )}
        <div className="t-flex t-justify-between t-mt-5">
          <div className="t-flex t-flex-col t-gap-1">
            <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-medium t-text-[#000000]">
              Payment method
            </Typography>
            <Can
              I={config.actions.edit}
              a={config.components.organizationSubscription}
            >
              <div
                className="t-flex t-gap-1 t-items-center t-cursor-pointer"
                onClick={() => setShowPaymentMethodModal(true)}
              >
                <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[#1BA1E2]">
                  {subscription?.IsPaymentMethodPresent
                    ? 'Change payment method'
                    : 'Add payment method'}
                </Typography>
                <RightOutlined
                  style={{ fontSize: '0.8rem' }}
                  className="t-text-[#1BA1E2]"
                />
              </div>
            </Can>
          </div>
          {subscription?.IsPaymentMethodPresent &&
            subscription?.PaymentProcessor === 'Stripe' &&
            subscription?.IsBankAccount === false && (
              <div className="t-flex t-flex-col t-items-end t-gap-1">
                <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-medium t-text-[#000000]">
                  Credit Card
                </Typography>
                <Typography className="t-text-roboto t-text-[16px] t-leading-[18.75px] t-font-medium t-text-[#000000]">
                  **** {subscription?.CardLastFour}
                </Typography>
              </div>
            )}
        </div>
      </div>
      <Divider />
      <Can
        I={config.actions.edit}
        a={config.components.organizationSubscription}
        passThrough
      >
        {(allowed) => (
          <div className="t-flex t-flex-col t-gap-4 t-px-[30px]">
            {subscription?.SubscriptionStatus === 'Active' && (
              <Popconfirm
                title="Are you sure you want to end your Extensia subscription?"
                onConfirm={() => handleCancelSubscription()}
                disabled={!allowed}
              >
                <Button
                  type="primary"
                  size="large"
                  className={`t-w-full t-h-[3rem] t-bg-[#F1F1F2] t-border-[#F1F1F2] t-border-solid t-border-[2px] t-text-[18px] t-leading-[22px] t-font-medium t-text-[#000000] ${
                    !allowed && 't-opacity-50'
                  }`}
                  loading={cancellingSubscription}
                  disabled={!allowed}
                >
                  End Subscription
                </Button>
              </Popconfirm>
            )}
            {(subscription?.SubscriptionStatus === 'Cancelled' ||
              subscription?.SubscriptionStatus === 'Expired') && (
              <Button
                type="primary"
                size="large"
                className={`t-w-full t-h-[3rem] t-bg-[#1BA1E2] t-border-[#1BA1E2] t-border-solid t-border-[2px] t-text-[18px] t-leading-[22px] t-font-medium t-text-[#FFFFFF] ${
                  !allowed && 't-opacity-50'
                }`}
                loading={renewingSubscription}
                disabled={!allowed}
                onClick={handleRenewSubscription}
              >
                Renew Subscription
              </Button>
            )}
          </div>
        )}
      </Can>
    </>
  )
}

export default InfoCard
